// «История параметров»: what changed between two versions of the estimate parameters, in the
// estimator's words — no field names, identifiers or «Не задано → Не задано».

export const kindNames = { labor: "Труд рабочих", machines: "Машины", drivers: "Труд машинистов", materials: "Материалы", equipment: "Оборудование" };

const names: Record<string, string> = {
  ...kindNames, method: "Метод", region: "Регион", price_level: "Уровень цен", source: "Основание",
  required_base: "База по согласованию с заказчиком", required_edition: "Редакция по согласованию с заказчиком",
  nrsp_territory: "Графа НР по 812/пр", confirmed: "Подтверждение", price_book_id: "Версия цен ресурсов",
  rate_book_id: "Версия карточек", default_policy: "Общие ставки", policies: "Ставки позиции",
  coefficients: "Коэффициенты", indices: "Индексы", overhead_percent: "НР, % ФОТ", profit_percent: "СП, % ФОТ",
  card_driver_per_norm: "Оплата машинистов", resource_quantities: "Расходы ресурсов", quantity: "Количество",
  unit: "Единица", additional_resources: "Отдельные позиции", work_id: "Работа", resource_index: "Ресурс ВОР",
  resource_fingerprint: "Основание ресурса", kind: "Статья затрат", reason: "Почему отдельно",
  replaces_application_id: "Заменяемое применение", replaces_code: "Заменяемый ресурс",
  covers_application_id: "Строка нормы в позиции", covers_code: "Код строки «Н»", excludes: "Исключение материала нормы",
  code: "Код ФСБЦ или ТЦ КАЦ", name: "Изделие", quantity_source: "Основание количества", price: "Цена КАЦ, ₽",
  price_source: "Основание цены", charges: "Начисления", percent: "Ставка, %", basis: "База начисления",
  vat_percent: "НДС, %", nrsp_selection: "Ставка, закреплённая по норме", application_id: "Применение нормы",
  norm_id: "Норма", territory: "Графа НР", resource_version_id: "Карточка ресурсов нормы",
  resource_sha256: "SHA-256 карточки", overhead: "НР", profit: "СП", row: "Строка таблицы",
  source_order: "Приказ", source_revision: "Редакция приказа", source_sha256: "SHA-256 источника",
  source_status_verified: "Действие редакции подтверждено",
};
const values: Record<string, string> = {
  resource_index: "Ресурсно-индексный", base_index: "Базисно-индексный", direct: "Прямые затраты", payroll: "ФОТ",
  with_overhead_profit: "Сумма с НР и СП", running_total: "С учётом предыдущих начислений", territory: "Территория",
  mprks: "МПРКС", rks: "РКС", ...kindNames,
};
const UUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/;

export type Names = { positions?: Record<string, string>; works?: Record<string, string> };
export type Change = { label: string; before: string; after: string };

const object = (value: unknown): Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};

function flatten(value: unknown, path: string[] = [], out: Record<string, unknown> = {}) {
  if (value !== null && typeof value === "object") for (const [key, item] of Object.entries(value)) flatten(item, [...path, key], out);
  else out[path.join("/")] = value;
  return out;
}

function show(key: string, value: unknown, known: Names): string {
  if (value == null || value === "") return "Не задано";
  const field = key.split("/").pop() ?? "";
  if (typeof value === "boolean") return field === "confirmed" ? (value ? "Подтверждено" : "Не подтверждено") : value ? "Да" : "Нет";
  const text = String(value);
  if (UUID.test(text)) {
    if (field === "work_id") return known.works?.[text] ?? `Работа ${text.slice(0, 8)}`;
    if (field.endsWith("application_id")) return known.positions?.[text] ?? `Позиция ${text.slice(0, 8)}`;
    return `№ ${text.slice(0, 8)}`;
  }
  return values[text] ?? text;
}

function label(key: string, known: Names): string {
  return key.split("/").map(part => known.positions?.[part] ?? names[part]
    ?? (/^\d+$/.test(part) ? `№ ${Number(part) + 1}` : UUID.test(part) ? `Позиция ${part.slice(0, 8)}` : part)).join(" · ");
}

/** Changed fields of two parameter versions; a position's own policy starts from the common one. */
export function parameterChanges(before: unknown, after: unknown, known: Names = {}): Change[] {
  const previous = object(before), following = object(after);
  const priorPolicies = object(previous.policies), nextPolicies = object(following.policies);
  const policyIds = [...new Set([...Object.keys(priorPolicies), ...Object.keys(nextPolicies)])];
  const effective = (parameters: Record<string, unknown>, policies: Record<string, unknown>) =>
    ({ ...parameters, policies: Object.fromEntries(policyIds.map(id => [id, policies[id] ?? parameters.default_policy ?? {}])) });
  const old = flatten(effective(previous, priorPolicies)), next = flatten(effective(following, nextPolicies));
  return [...new Set([...Object.keys(old), ...Object.keys(next)])]
    .map(key => ({ label: label(key, known), before: show(key, old[key], known), after: show(key, next[key], known) }))
    .filter(change => change.before !== change.after);
}
